import { useEffect, useRef } from "react";

// Mémoire de défilement par vue.
//
// Le problème : revenir d'une extension vers l'accueil (ou du Pokédex vers une
// fiche puis retour) renvoyait toujours tout en haut. Avec 200 extensions ou
// 1025 espèces, on perdait sa place à chaque aller-retour.
//
// Le principe : chaque vue a une clé (« home », « pokedex », « set:swsh12 »…).
// On enregistre en continu la position de la vue affichée, et on la restaure
// quand cette même clé revient. Une vue jamais visitée démarre en haut.
//
// ⚠️ La restauration ne peut pas être immédiate : au moment où la vue change,
// son contenu n'est pas encore rendu (et pour le Pokédex ou une extension, les
// données arrivent même après un aller-retour réseau). La page est donc trop
// courte pour qu'un `scrollTo` porte. D'où la ré-tentative sur plusieurs frames
// tant que la hauteur ne suffit pas, abandonnée au bout de RESTORE_TIMEOUT_MS.
const RESTORE_TIMEOUT_MS = 1500;

// Volontairement hors du composant : la mémoire doit survivre aux démontages
// de vues, c'est tout l'intérêt.
const positions = new Map();

export function useScrollMemory(viewKey) {
  const currentKey = useRef(viewKey);

  // Enregistrement continu de la position de la vue courante.
  useEffect(() => {
    const onScroll = () => positions.set(currentKey.current, window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    currentKey.current = viewKey;
    const target = positions.get(viewKey) || 0;

    if (target === 0) {
      window.scrollTo(0, 0);
      return undefined;
    }

    let frame;
    const start = Date.now();
    const tryRestore = () => {
      const reachable = document.documentElement.scrollHeight - window.innerHeight;
      if (reachable >= target) {
        window.scrollTo(0, target);
        return;
      }
      if (Date.now() - start < RESTORE_TIMEOUT_MS) {
        frame = requestAnimationFrame(tryRestore);
      } else {
        // Le contenu ne reviendra pas à sa taille d'avant (filtre changé,
        // carte retirée…) : on se contente du plus bas atteignable.
        window.scrollTo(0, Math.max(0, reachable));
      }
    };
    frame = requestAnimationFrame(tryRestore);
    return () => cancelAnimationFrame(frame);
  }, [viewKey]);
}
